// Renders a `netwatch-chart` block emitted by the assistant. The block may be:
//
//   • the legacy single-series shape — {title, unit, threshold, points:[{t,y}]}
//     — which still renders exactly as it always has;
//   • the extended schema — a `type` plus `series[]` (and `labels[]` for
//     categorical charts), i.e. multiple metrics, bars, pies, scatter;
//   • a `ref` the backend expands into one of the above before the answer is
//     persisted. If a block still carries a bare ref (mid-stream, or an old
//     answer whose data is long gone), we say so rather than dumping JSON.
//
// All the decisions live in chartSpec/chartOptions; this component owns the
// caption, the fallback ladder and nothing else.

import { useMemo, useState } from 'react'
import type { Chart as ChartJSInstance } from 'chart.js'

import { ChartActions } from './ChartActions'
import { ChartDialog } from './ChartDialog'
import { NwChart } from './NwChart'
import { normalizeChartBlock, type ChartBlock, type ChartPayload } from './chartSpec'

/** Legacy point shape, still exported for hosts that typed against it. */
export interface ChartPoint {
  t?: number
  x?: number | string
  y: number
}

export interface ChartData extends ChartBlock {
  points?: ChartPoint[]
}

interface NetwatchChartProps {
  data: ChartData | null | undefined
  raw?: string
  /** Series resolved for a `ref`, when the host has them. */
  payload?: ChartPayload | null
  /** The turn is still streaming — an unresolved ref is expected, not broken. */
  busy?: boolean
}

const RAW_LIMIT = 1024

export function NetwatchChart({ data, raw, payload, busy }: NetwatchChartProps) {
  const result = useMemo(() => normalizeChartBlock(data, payload), [data, payload])
  const [expanded, setExpanded] = useState(false)
  // Held so the PNG export can snapshot the live canvas on its own surface.
  const [chart, setChart] = useState<{ instance: ChartJSInstance; surface: string } | null>(null)

  if (!result.ok) {
    // A ref with no data yet: while the turn streams, the block is simply
    // ahead of its payload — show the space it will occupy, not an error.
    if (result.reason === 'chart data not loaded') {
      return busy
        ? <div className="nw-nchart-wrap"><div className="nw-nchart-skel" style={{ height: 180 }} /></div>
        : <div className="nw-nchart-wrap"><div className="nw-nchart-note">Chart data unavailable.</div></div>
    }
    // Malformed or impossible. Always a plain-language note — a client-facing
    // bubble never shows bare JSON (a hand-authored block once leaked its raw
    // points as scrolling text). The source stays reachable behind a
    // disclosure for whoever needs to debug it.
    const text = raw ?? ''
    return (
      <div className="nw-nchart-wrap">
        <div className="nw-nchart-note">Couldn’t draw this chart — {result.reason}. Ask me to re-draw it.</div>
        {text.length > 0 && (
          <details className="nw-nchart-rawwrap"><summary>Show data</summary><pre className="nw-nchart-raw">{text.slice(0, 4 * RAW_LIMIT)}</pre></details>
        )}
      </div>
    )
  }

  const { spec } = result
  return (
    <div className="nw-nchart-wrap">
      {/* The header row exists even for an untitled chart — it carries the
          expand affordance, and a chat bubble is too narrow to read a 24h
          two-series line properly. */}
      <div className="nw-nchart-title">
        <span className="nw-nchart-title-text">{spec.title}</span>
        <ChartActions spec={spec} chart={chart} onExpand={() => setExpanded(true)} compact />
      </div>
      <NwChart
        spec={spec}
        onReady={(instance, surface) => setChart({ instance, surface })}
        inkVar="--nw-text"
        faintVar="--nw-text-tertiary"
        gridVar="--nw-border"
        thresholdVar="--nw-danger"
        surfaceVar="--nw-bg"
      />
      {spec.notes.length > 0 && <div className="nw-nchart-more">{spec.notes.join(' · ')}</div>}
      {expanded && <ChartDialog spec={spec} onClose={() => setExpanded(false)} />}
    </div>
  )
}
